import { useState, useEffect } from 'react';
import BasicSEO from '../../Components/SEO/BasicSEO';

import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Avatar,
  Button,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Alert,
  Stack,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Fab,
  SwipeableDrawer,
  Chip,
  Badge,
  Tabs,
  Tab,
  Collapse
} from '@mui/material';

import {
  Search as SearchIcon,
  PeopleOutlined as PeopleIcon,
  Business as BusinessIcon,
  RateReview as ReviewIcon,
  TrendingUp as TrendingIcon,
  LocationOnOutlined as LocationIcon,
  StorefrontOutlined as StorefrontIcon,
  ArrowForward as ArrowIcon,
  RateReview as RateReviewIcon,
  AddBusiness as AddBusinessIcon,
  TrendingUp,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  AccountCircle as ProfileIcon,
  Assignment as TasksIcon,
  Lightbulb as SuggestionsIcon,
  Flag as GoalsIcon,
  AutoAwesome as AIIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  Google as GoogleIcon
} from '@mui/icons-material';

import FacebookIcon  from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon  from '@mui/icons-material/LinkedIn';

import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUserByEmailQuery } from '../../redux/thunks/users/getuserbyemail';
import { useGetUserProfileQuery } from '../../redux/thunks/users/userprofile';
import { useGetSocialAccountsQuery } from '../../redux/thunks/ai/getsocialaccounts';
import LocationModal from './LocationModal';

// ===== Constants =====

const GETTING_STARTED_ITEMS = [
  {
    id:          'find-people',
    title:       'Find People You Know',
    icon:        PeopleIcon,
    description: 'Connect with your network',
    action:      'Find People',
    completed:   false,
    color:       'primary'
  },
  {
    id:          'set-location',
    title:       'Set Your Location',
    icon:        LocationIcon,
    description: 'Get relevant local recommendations',
    action:      'Set Location',
    completed:   false,
    hidden:      false,
    color:       'warning'
  },
  {
    id:          'find-businesses',
    title:       'Find Businesses',
    icon:        StorefrontIcon,
    description: 'Discover trusted local businesses',
    action:      'Explore',
    completed:   false,
    color:       'success'
  }
];

const SUGGESTED_ACTIONS = [
  {
    id:          'trusted-businesses',
    title:       'Businesses Your Connections Trust',
    description: 'See where your network shops and dines',
    count:       12,
    action:      'View All',
    goal:        3,
    isUnlocked:  (connections) => connections >= 3,
    goalMessage: 'Goal: At least 3 connections',
  },
  {
    id:          'local-influencers',
    title:       'Local Influencers',
    description: 'Connect with trusted reviewers in your area',
    count:       8,
    action:      'Discover',
    goal:        'location',
    isUnlocked:  (location) => Boolean(location),
    goalMessage: 'Goal: Set your address',
  },
  {
    id:          'review-insights',
    title:       'Review Insights',
    description: 'Analyze your review performance',
    count:       5,
    action:      'Start Reviewing',
    goal:        1,
    isUnlocked:  (reviews) => reviews >= 1,
    goalMessage: 'Goal: Write at least 1 review',
  },
];

const NEXT_GOALS = [
  {
    id:          1,
    title:       'Write First Review',
    description: 'Share your experience',
    current:     0,
    target:      1,
    color:       'primary',
    icon:        ReviewIcon
  },
  {
    id:          2,
    title:       'Build Your Network',
    description: 'Connect with 5 people',
    current:     1,
    target:      5,
    color:       'success',
    icon:        PeopleIcon
  },
  {
    id:          3,
    title:       'Follow Businesses',
    description: 'Follow 10 trusted businesses',
    current:     3,
    target:      10,
    color:       'warning',
    icon:        BusinessIcon
  }
];

const DISCOVERY_MAIN_ACTIONS = [
  {
    icon:        SearchIcon,
    title:       'Discover and Review',
    description: 'Share your experiences with local businesses',
    action:      (navigate) => navigate('/find'),
    disabled:    false
  },
  {
    icon:        PeopleIcon,
    title:       'Connect with Others',
    description: 'Build your trust network',
    action:      (navigate) => navigate('/network'),
    disabled:    false
  },
  {
    icon:        BusinessIcon,
    title:       'Own a Business?',
    description: 'Claim and manage your business profile',
    action:      (navigate) => navigate('/company/new'),
    disabled:    false
  },
  {
    icon:        AddBusinessIcon,
    title:       'Request Reviews',
    description: 'Post a product or business to get reviews',
    action:      () => {},
    disabled:    true,
    comingSoon:  true
  }
];

const BUSINESS_MAIN_ACTIONS = [
  {
    icon:        AddBusinessIcon,
    title:       'Add Your Business',
    description: 'Create your business profile and unlock AI content generation',
    action:      (navigate) => navigate('/company/new'),
    disabled:    false,
    primary:     true
  },
  {
    icon:        SearchIcon,
    title:       'Find & Claim a Business',
    description: 'Search for your existing business and claim it',
    action:      (navigate) => navigate('/find'),
    disabled:    false
  },
  {
    icon:        PeopleIcon,
    title:       'Build Your Network',
    description: 'Connect with customers and local businesses',
    action:      (navigate) => navigate('/network'),
    disabled:    false
  },
  {
    icon:        StorefrontIcon,
    title:       'Explore the Platform',
    description: 'See how Revfer works for businesses like yours',
    action:      (navigate) => navigate('/find'),
    disabled:    false
  }
];

const BUSINESS_SETUP_GOALS = [
  {
    id:          'add-business',
    title:       'Add Your Business',
    description: 'Create your business profile',
    locked:      false,
    icon:        AddBusinessIcon
  },
  {
    id:          'connect-facebook',
    title:       'Connect Facebook',
    description: 'Post content automatically',
    locked:      true,
    icon:        FacebookIcon
  },
  {
    id:          'connect-instagram',
    title:       'Connect Instagram',
    description: 'Share posts and stories',
    locked:      true,
    icon:        InstagramIcon
  },
  {
    id:          'connect-linkedin',
    title:       'Connect LinkedIn',
    description: 'Reach professional audience',
    locked:      true,
    icon:        LinkedInIcon
  },
  {
    id:          'connect-google',
    title:       'Connect Google Reviews',
    description: 'Respond to Google reviews with AI',
    locked:      true,
    icon:        GoogleIcon
  }
];

// ===== Helper functions =====

const getNextGoals = (userData) => {
  if (!userData) return NEXT_GOALS;
  return [
    {
      id:          1,
      title:       'Write First Review',
      description: 'Share your experience',
      current:     userData.stats?.reviews?.totalReviews || 0,
      target:      1,
      color:       'primary',
      icon:        ReviewIcon
    },
    {
      id:          2,
      title:       'Build Your Network',
      description: 'Connect with 5 people',
      current:     userData.firstDegreeFriends?.length || 0,
      target:      5,
      color:       'success',
      icon:        PeopleIcon
    },
    {
      id:          3,
      title:       'Follow Businesses',
      description: 'Follow 10 trusted businesses',
      current:     userData.stats?.follow?.totalFollows || 0,
      target:      10,
      color:       'warning',
      icon:        BusinessIcon
    }
  ];
};

const getGettingStartedItems = (userData) => {
  if (!userData) return GETTING_STARTED_ITEMS;
  return [
    {
      id:          'find-people',
      title:       'Find People You Know',
      icon:        PeopleIcon,
      description: 'Connect with your network',
      action:      'Find People',
      completed:   userData.firstDegreeFriends?.length > 1,
      color:       'primary'
    },
    {
      id:          'set-location',
      title:       'Set Your Location',
      icon:        LocationIcon,
      description: 'Get relevant local recommendations',
      action:      'Set Location',
      completed:   userData.geocodeResults && Object.keys(userData.geocodeResults).length > 0,
      hidden:      false,
      color:       'warning'
    },
    {
      id:          'find-businesses',
      title:       'Find Businesses',
      icon:        StorefrontIcon,
      description: 'Discover trusted local businesses',
      action:      'Explore',
      completed:   (
        (userData.myTrustedThings?.length > 1) ||
        (userData.following?.length > 1) ||
        (userData.stats?.reviews?.totalReviews > 1)
      ),
      color:       'success'
    }
  ];
};

// ===== Skeleton components =====

const ProfileSkeleton = () => (
  <Box sx={{ p: 2 }}>
    <Skeleton variant="circular" width={80} height={80} sx={{ mb: 2 }} />
    <Skeleton variant="text" width={150} />
    <Skeleton variant="text" width={100} />
  </Box>
);

const ContentSkeleton = () => (
  <Box sx={{ p: 2 }}>
    <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
    <Skeleton variant="rectangular" height={80}  sx={{ mb: 2 }} />
    <Skeleton variant="rectangular" height={200} />
  </Box>
);

// ===== View Toggle Tabs =====

const ViewToggle = ({ view, onChange }) => (
  <Tabs
    value={view}
    onChange={(_, v) => onChange(v)}
    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
  >
    <Tab
      value="discovery"
      label="Discover"
      icon={<SearchIcon fontSize="small" />}
      iconPosition="start"
    />
    <Tab
      value="business"
      label="Business Owner"
      icon={<BusinessIcon fontSize="small" />}
      iconPosition="start"
    />
  </Tabs>
);

// ===== Stats Banners =====

const MobileStatsBanner = ({ user }) => {
  const StatCard = ({ title, value, icon: Icon }) => (
    <Box sx={{ textAlign: 'center', minWidth: 80 }}>
      <Box sx={{
        bgcolor:        'rgba(255,255,255,0.1)',
        p:              1,
        borderRadius:   2,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        mb:             1,
        mx:             'auto',
        width:          40,
        height:         40
      }}>
        <Icon sx={{ color: 'white', fontSize: 20 }} />
      </Box>
      <Typography variant="h6" color="white" sx={{ fontWeight: 600 }}>{value}</Typography>
      <Typography variant="caption" color="rgba(255,255,255,0.7)">{title}</Typography>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', bgcolor: 'primary.main', color: 'white', py: 2, mb: 3 }}>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: 1 }}>
          <StatCard title="Trust Score" value={user.trustScore || 0}                       icon={TrendingIcon}   />
          <StatCard title="Reviews"    value={user.stats?.reviews?.totalReviews || 0}       icon={RateReviewIcon} />
          <StatCard title="Network"    value={user.firstDegreeFriends?.length || 0}         icon={PeopleIcon}     />
        </Box>
      </Container>
    </Box>
  );
};

const DesktopStatsBanner = ({ user }) => {
  const theme = useTheme();
  const StatCard = ({ title, value, icon: Icon }) => (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon sx={{ color: 'white' }} />
      </Box>
      <Box>
        <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>{title}</Typography>
        <Typography variant="h6" color="white">{value}</Typography>
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ width: '100%', bgcolor: 'primary.main', color: 'white', py: 3, mb: 3 }}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard title="Trust Score" value={user.trustScore || 0}                     icon={TrendingIcon}   />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Reviews"    value={user.stats?.reviews?.totalReviews || 0}     icon={RateReviewIcon} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Network"    value={user.firstDegreeFriends?.length || 0}       icon={PeopleIcon}     />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const BusinessWelcomeBanner = ({ isMobile }) => (
  <Box sx={{ width: '100%', bgcolor: 'primary.main', color: 'white', py: isMobile ? 2 : 3, mb: 3 }}>
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <AIIcon sx={{ fontSize: isMobile ? 32 : 44, color: 'rgba(255,255,255,0.9)' }} />
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h5'} color="white" fontWeight={600}>
            Welcome, Business Owner
          </Typography>
          <Typography variant={isMobile ? 'body2' : 'body1'} color="rgba(255,255,255,0.85)">
            Add your business to unlock AI content generation, review management and more
          </Typography>
        </Box>
      </Box>
    </Container>
  </Box>
);

// ===== Connect nudge (inside My Businesses Banner) =====
// Separate component so the hook is not called after MyBusinessesBanner's
// early return. Social accounts are per user, so one nudge covers every business.
const ConnectNudge = ({ businesses, navigate }) => {
  const { data: social, isLoading } = useGetSocialAccountsQuery();
  const connected = (social?.accounts || []).some(a => a.isHealthy);

  if (isLoading || connected) {
    return (
      <Typography variant="caption" color="text.secondary">
        Drafts, posting and integrations are on your business page.
      </Typography>
    );
  }
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Next step: connect Facebook or Instagram so Revfer can post what you approve.
      </Typography>
      <Button
        fullWidth
        variant="contained"
        startIcon={<FacebookIcon />}
        onClick={() => navigate(`/business/${businesses[0]._id}/social/accounts`)}
        sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        Connect Facebook & Instagram
      </Button>
    </Box>
  );
};

// ===== My Businesses Banner =====

const MyBusinessesBanner = ({ businesses, navigate }) => {
  if (!businesses || businesses.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{
      p:           2,
      mb:          2,
      border:      '2px solid',
      borderColor: 'primary.main',
      borderRadius: 2,
      bgcolor:     'primary.50'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AIIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700} color="primary.main">
          Your Business{businesses.length > 1 ? 'es' : ''}
        </Typography>
      </Box>

      {businesses.map((biz) => (
        <Paper
          key={biz._id}
          elevation={0}
          onClick={() => navigate(`/business/${biz._id}/${biz.businessName?.replace(/[\s,\.]+/g, '-').toLowerCase()}`)}
          sx={{
            p:              1.5,
            mb:             1,
            bgcolor:        'background.paper',
            cursor:         'pointer',
            borderRadius:   1,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            '&:hover':      { bgcolor: 'grey.50' }
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>{biz.businessName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {biz.skillName || biz.category?.[0] || 'Business'}
            </Typography>
          </Box>
          <OpenInNewIcon fontSize="small" color="action" />
        </Paper>
      ))}

      <ConnectNudge businesses={businesses} navigate={navigate} />
    </Paper>
  );
};

// ===== Business Profile Panel (left column) =====

const BusinessProfilePanel = ({ user, navigate, getInitials }) => (
  <Box>
    <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
        onClick={() => navigate(`/profile/${user._id}/active`)}
      >
        <Avatar sx={{ width: 80, height: 80, mr: 2 }} src={user.profileImageUrl || undefined} alt={user.displayName}>
          {!user.profileImageUrl && getInitials(user.displayName)}
        </Avatar>
        <Box>
          <Typography variant="h6">{user.displayName}</Typography>
          <Typography variant="body2" color="text.secondary">Business Owner</Typography>
        </Box>
      </Box>
      <LinearProgress variant="determinate" value={user.profileComplete || 0} sx={{ mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        {user.profileComplete || 0}% Profile Complete
      </Typography>
    </Paper>

    <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AIIcon color="primary" fontSize="small" />
        Why Revfer for Business?
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
        {[
          'AI writes content drafts automatically',
          'One-click approve and publish',
          'Respond to reviews without typing',
          'FAQs generated from customer questions',
          'Scheduled posts for all platforms',
        ].map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main', mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body2">{item}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  </Box>
);

// ===== Business Setup Checklist (right column) =====

const BusinessSetupChecklist = () => (
  <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
    <Typography variant="h6" gutterBottom>Your Setup Checklist</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Complete these to get the most out of Revfer
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {BUSINESS_SETUP_GOALS.map((goal) => {
        const Icon = goal.icon;
        return (
          <Box
            key={goal.id}
            sx={{
              display:      'flex',
              alignItems:   'center',
              gap:          1.5,
              p:            1.5,
              borderRadius: 2,
              bgcolor:      goal.locked ? 'grey.50' : 'primary.50',
              border:       '1px solid',
              borderColor:  goal.locked ? 'grey.200' : 'primary.main',
              opacity:      goal.locked ? 0.6 : 1
            }}
          >
            <Box sx={{
              width:          36,
              height:         36,
              borderRadius:   '50%',
              bgcolor:        goal.locked ? 'grey.200' : 'primary.main',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              flexShrink:     0
            }}>
              <Icon sx={{ color: goal.locked ? 'text.disabled' : 'white', fontSize: 18 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>{goal.title}</Typography>
              <Typography variant="caption" color="text.secondary">{goal.description}</Typography>
            </Box>
            {goal.locked
              ? <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              : <ArrowIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            }
          </Box>
        );
      })}
    </Box>

    <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon fontSize="small" />}>
      <Typography variant="caption">
        Connect your social accounts and Google Reviews from your business page after adding your business.
      </Typography>
    </Alert>
  </Paper>
);

// ===== Discovery Getting Started =====

const GetStartedSection = ({ onAction, userData }) => {
  const [showLocationAlert, setShowLocationAlert] = useState(true);
  const items          = getGettingStartedItems(userData);
  const completedSteps = items.filter(item => item.completed).length;
  const totalSteps     = items.length;

  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>Getting Started</Typography>

      <Collapse in={!items[1].completed && showLocationAlert}>
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowLocationAlert(false)}>
          Set your location to get personalized recommendations
        </Alert>
      </Collapse>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item) => {
          if (item.hidden) return null;
          const Icon = item.icon;
          return (
            <Paper
              key={item.id}
              elevation={0}
              onClick={() => !item.completed && onAction(item.id)}
              sx={{
                p:           2,
                bgcolor:     `${item.color}.50`,
                borderRadius: 2,
                cursor:      item.completed ? 'default' : 'pointer',
                transition:  'all 0.3s ease',
                '&:hover':   {
                  bgcolor:   item.completed ? `${item.color}.50` : `${item.color}.100`,
                  transform: item.completed ? 'none' : 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ bgcolor: `${item.color}.main`, borderRadius: '50%', p: 1, mr: 2, display: 'flex', opacity: item.completed ? 0.7 : 1 }}>
                  <Icon sx={{ color: 'white' }} />
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle1" sx={{ opacity: item.completed ? 0.7 : 1 }}>
                    {item.title}
                    {item.completed && (
                      <Typography component="span" sx={{ ml: 1, color: 'success.main', fontSize: '0.8rem' }}>
                        ✓ Done
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ opacity: item.completed ? 0.7 : 1 }}>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {completedSteps} of {totalSteps} steps completed
        </Typography>
        {completedSteps === totalSteps && (
          <Typography variant="body2" color="success.main">All set! ✨</Typography>
        )}
      </Box>
    </Paper>
  );
};

// ===== Main Actions Section =====

const MainActionsSection = ({ actions }) => {
  const navigate = useNavigate();
  const isBusiness = actions === BUSINESS_MAIN_ACTIONS;

  return (
    <Paper variant="outlined" sx={{ p: 3, border: 'none' }}>
      <Typography variant="h5" gutterBottom>
        {isBusiness ? 'Grow Your Business' : 'Start Building Your Network'}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Here is what you can do:
      </Typography>

      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {actions.map((action, index) => {
          const Icon = action.icon;
          return (
            <Paper
              key={index}
              elevation={0}
              onClick={() => !action.disabled && action.action(navigate)}
              sx={{
                p:           action.primary ? 3 : 2.5,
                bgcolor:     action.primary ? 'primary.50' : 'grey.50',
                borderRadius: 2,
                border:      action.primary ? '2px solid' : 'none',
                borderColor: 'primary.main',
                cursor:      action.disabled ? 'default' : 'pointer',
                transition:  'all 0.2s ease',
                opacity:     action.disabled ? 0.7 : 1,
                '&:hover':   {
                  bgcolor:   action.disabled
                    ? (action.primary ? 'primary.50' : 'grey.50')
                    : (action.primary ? 'primary.100' : 'grey.100'),
                  transform: action.disabled ? 'none' : 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{
                  minWidth:       48,
                  height:         48,
                  borderRadius:   2,
                  bgcolor:        action.primary ? 'primary.main' : 'primary.50',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  mr:             2
                }}>
                  <Icon sx={{ color: action.primary ? 'white' : 'primary.main', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: action.primary ? 700 : 500 }}>
                      {action.title}
                    </Typography>
                    {action.comingSoon && (
                      <Typography variant="caption" sx={{ px: 1, py: 0.5, bgcolor: 'warning.light', color: 'warning.dark', borderRadius: 1 }}>
                        Coming Soon
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

// ===== Next Goals =====

const NextGoalsSection = ({ userData }) => {
  const goals = getNextGoals(userData);
  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>Next Goals</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Complete these to level up your profile
      </Typography>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {goals.map((goal) => (
          <Paper key={goal.id} variant="outlined" sx={{ p: 2, border: 'none', bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: `${goal.color}.light`, color: `${goal.color}.main` }}>
                {goal.id}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{goal.title}</Typography>
                <Typography variant="body2" color="text.secondary">{goal.description}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min((goal.current / goal.target) * 100, 100)}
                sx={{ flex: 1, height: 6, borderRadius: 3, [`& .MuiLinearProgress-bar`]: { bgcolor: `${goal.color}.main` } }}
              />
              <Typography variant="caption" color="text.secondary">
                {goal.current}/{goal.target}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};

// ===== Mobile Dashboard Sidebar =====

const MobileDashboardSidebar = ({
  isOpen, onClose, user, userData,
  navigate, onAction, handleSuggestionAction, view
}) => {
  const [expandedSections, setExpandedSections] = useState({
    profile: true, tasks: false, checklist: false, goals: false, suggestions: false
  });

  const toggleSection = (section) =>
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  const getInitials = (name) => {
    if (!name) return '';
    const [firstName, lastName] = name.split(' ');
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  };

  const items          = getGettingStartedItems(userData);
  const completedTasks = items.filter(item => item.completed).length;
  const totalTasks     = items.length;
  const goals          = getNextGoals(userData);
  const businesses     = userData?.myBusinesses || [];

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{ sx: { width: '100%', maxWidth: 400, bgcolor: 'background.default' } }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Dashboard Menu</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>

        {/* My Businesses — business view */}
        {view === 'business' && businesses.length > 0 && (
          <MyBusinessesBanner
            businesses={businesses}
            navigate={(path) => { navigate(path); onClose(); }}
          />
        )}

        {/* Profile */}
        <Card sx={{ mb: 2 }}>
          <CardContent
            sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => toggleSection('profile')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ProfileIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Profile</Typography>
            </Box>
            {expandedSections.profile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.profile}>
            <CardContent sx={{ pt: 0 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
                onClick={() => { navigate(`/profile/${user._id}/active`); onClose(); }}
              >
                <Avatar sx={{ width: 60, height: 60, mr: 2 }} src={user.profileImageUrl || undefined} alt={user.displayName}>
                  {!user.profileImageUrl && getInitials(user.displayName)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">{user.displayName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {view === 'business' ? 'Business Owner' : 'New Member'}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress variant="determinate" value={user.profileComplete || 0} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {user.profileComplete || 0}% Complete
              </Typography>
            </CardContent>
          </Collapse>
        </Card>

        {/* Business view — setup checklist */}
        {view === 'business' && (
          <Card sx={{ mb: 2 }}>
            <CardContent
              sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={() => toggleSection('checklist')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TasksIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Setup Checklist</Typography>
              </Box>
              {expandedSections.checklist ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            <Collapse in={expandedSections.checklist}>
              <CardContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {BUSINESS_SETUP_GOALS.map((goal) => {
                    const Icon = goal.icon;
                    return (
                      <Box
                        key={goal.id}
                        sx={{
                          display:      'flex',
                          alignItems:   'center',
                          gap:          1.5,
                          p:            1.5,
                          borderRadius: 2,
                          bgcolor:      goal.locked ? 'grey.50' : 'primary.50',
                          border:       '1px solid',
                          borderColor:  goal.locked ? 'grey.200' : 'primary.main',
                          opacity:      goal.locked ? 0.6 : 1
                        }}
                      >
                        <Box sx={{
                          width:          32,
                          height:         32,
                          borderRadius:   '50%',
                          bgcolor:        goal.locked ? 'grey.200' : 'primary.main',
                          display:        'flex',
                          alignItems:     'center',
                          justifyContent: 'center',
                          flexShrink:     0
                        }}>
                          <Icon sx={{ color: goal.locked ? 'text.disabled' : 'white', fontSize: 16 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{goal.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{goal.description}</Typography>
                        </Box>
                        {goal.locked
                          ? <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          : <ArrowIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        }
                      </Box>
                    );
                  })}
                </Box>
                <Alert severity="info" sx={{ mt: 1.5 }} icon={<InfoIcon fontSize="small" />}>
                  <Typography variant="caption">
                    Connect social accounts from your business page after adding your business.
                  </Typography>
                </Alert>
              </CardContent>
            </Collapse>
          </Card>
        )}

        {/* Discovery view — getting started tasks */}
        {view === 'discovery' && (
          <Card sx={{ mb: 2 }}>
            <CardContent
              sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={() => toggleSection('tasks')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TasksIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Getting Started</Typography>
                <Badge badgeContent={`${completedTasks}/${totalTasks}`} color="primary" sx={{ ml: 1 }} />
              </Box>
              {expandedSections.tasks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            <Collapse in={expandedSections.tasks}>
              <CardContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {items.map((item) => {
                    if (item.hidden) return null;
                    const Icon = item.icon;
                    return (
                      <Paper
                        key={item.id}
                        elevation={0}
                        onClick={() => { if (!item.completed) { onAction(item.id); onClose(); } }}
                        sx={{
                          p:           2,
                          bgcolor:     `${item.color}.50`,
                          borderRadius: 2,
                          cursor:      item.completed ? 'default' : 'pointer',
                          opacity:     item.completed ? 0.7 : 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ bgcolor: `${item.color}.main`, borderRadius: '50%', p: 1, mr: 2, display: 'flex', opacity: item.completed ? 0.7 : 1 }}>
                            <Icon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ opacity: item.completed ? 0.7 : 1 }}>
                              {item.title}
                              {item.completed && (
                                <Typography component="span" sx={{ ml: 1, color: 'success.main', fontSize: '0.75rem' }}>
                                  ✓ Done
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ opacity: item.completed ? 0.7 : 1 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        )}

        {/* Goals */}
        <Card sx={{ mb: 2 }}>
          <CardContent
            sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => toggleSection('goals')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GoalsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Next Goals</Typography>
            </Box>
            {expandedSections.goals ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.goals}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {goals.map((goal) => (
                  <Paper key={goal.id} variant="outlined" sx={{ p: 2, border: 'none', bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: `${goal.color}.light`, color: `${goal.color}.main`, fontSize: '0.75rem' }}>
                        {goal.id}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">{goal.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{goal.description}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((goal.current / goal.target) * 100, 100)}
                        sx={{ flex: 1, height: 6, borderRadius: 3, [`& .MuiLinearProgress-bar`]: { bgcolor: `${goal.color}.main` } }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {goal.current}/{goal.target}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Discovery — suggested actions */}
        {view === 'discovery' && (
          <Card sx={{ mb: 2 }}>
            <CardContent
              sx={{ pb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={() => toggleSection('suggestions')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SuggestionsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Suggestions</Typography>
              </Box>
              {expandedSections.suggestions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            <Collapse in={expandedSections.suggestions}>
              <CardContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {SUGGESTED_ACTIONS.map((action, index) => {
                    const isUnlocked = action.isUnlocked(0);
                    return (
                      <Paper
                        key={index}
                        variant="outlined"
                        onClick={() => { if (isUnlocked) { handleSuggestionAction(action.id); onClose(); } }}
                        sx={{
                          p:        2,
                          border:   'none',
                          bgcolor:  isUnlocked ? 'grey.50' : 'grey.200',
                          cursor:   isUnlocked ? 'pointer' : 'not-allowed',
                          opacity:  isUnlocked ? 1 : 0.6
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: isUnlocked ? 600 : 400 }}>
                          {action.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {action.description}
                        </Typography>
                        {!isUnlocked && (
                          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                            {action.goalMessage}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        )}

      </Box>
    </SwipeableDrawer>
  );
};

// ===== Main Component =====

export default function EarlyUserHome() {
  const navigate     = useNavigate();
  const locationData = useLocation();
  const theme        = useTheme();
  const isMobile     = useMediaQuery(theme.breakpoints.down('md'));

  const [sidebarOpen,       setSidebarOpen]       = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [userData,          setUserData]          = useState('');

  const user   = useSelector((state) => state.auth?.user   || '');
  const userId = useSelector((state) => state.auth?.userId || '');

  const { data: userProfileData, isLoading: isUserLoading } = useGetUserByEmailQuery(user.email);

  useEffect(() => {
    if (userProfileData) setUserData(userProfileData);
  }, [userProfileData]);

  // ===== Intent detection =====
  const searchParams  = new URLSearchParams(locationData.search);
  const urlIntent     = searchParams.get('intent');
  // myBusinesses is not on the users document (getUserByEmail never returns it);
  // the profile endpoint does, same as HomePage uses.
  const { data: ownerProfile } = useGetUserProfileQuery(userId, { skip: !userId });
  const myBusinesses  = ownerProfile?.myBusinesses || [];
  const hasBusinesses = myBusinesses.length > 0;

  // Goal chosen at onboarding persists on users.goals; without it a returning
  // business-intent user (no ?intent param) landed on the Discover tab.
  const wantsBusiness = Array.isArray(userProfileData?.goals) && userProfileData.goals.includes('business');

  const [view, setView] = useState(
    urlIntent === 'business' || hasBusinesses || wantsBusiness ? 'business' : 'discovery'
  );

  // Auto-detect when profile loads
  useEffect(() => {
    if ((hasBusinesses || wantsBusiness) && view !== 'business') {
      setView('business');
    }
  }, [userProfileData, ownerProfile]);

  const getInitials = (name) => {
    if (!name) return '';
    const [firstName, lastName] = name.split(' ');
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  };

  const handleGetStartedAction = (itemId) => {
    switch (itemId) {
      case 'add-business':    navigate('/company/new'); break;
      case 'find-people':     navigate('/network');     break;
      case 'set-location':    setLocationModalOpen(true); break;
      case 'find-businesses': navigate('/find');        break;
      default: break;
    }
  };

  const handleSuggestionAction = (itemId) => {
    switch (itemId) {
      case 'trusted-businesses': navigate('/network/trusted-businesses'); break;
      case 'local-influencers':  navigate('/network/influencers');        break;
      case 'first-reviews':      navigate('/reviews/opportunities');      break;
      default: break;
    }
  };

  const handleLocationSet = async (data) => {
    console.log('locationData', data);
  };

  const seoData = {
    title:       `Welcome — Getting Started with Revfer`,
    description: `Complete your Revfer setup and start ${view === 'business' ? 'growing your business' : 'building your network'}.`
  };

  if (isUserLoading) {
    return (
      <>
        <BasicSEO title="Loading Your Dashboard - Revfer" description="Setting up your personalized dashboard." noIndex={true} />
        <Box>
          <Paper variant="outlined" sx={{ width: '100%', p: 3, bgcolor: 'primary.main', borderRadius: 0, border: 'none' }}>
            <Container maxWidth="xl">
              <Skeleton variant="text" width={300} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
              <Skeleton variant="text" width={400} height={30} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            </Container>
          </Paper>
          <Container maxWidth="xl" sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}><Paper><ProfileSkeleton /></Paper></Grid>
              <Grid item xs={12} md={6}><Paper><ContentSkeleton /></Paper></Grid>
              <Grid item xs={12} md={3}><Paper><ContentSkeleton /></Paper></Grid>
            </Grid>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <BasicSEO title={seoData.title} description={seoData.description} noIndex={true} />

      <Box>

        {/* ===== Banner — switches based on view ===== */}
        {view === 'business'
          ? <BusinessWelcomeBanner isMobile={isMobile} />
          : isMobile
            ? <MobileStatsBanner user={userData} />
            : <DesktopStatsBanner user={userData} />
        }

        <Container maxWidth="xl" sx={{ mt: 3, px: { xs: 2, sm: 3 } }}>

          {/* ===== View Toggle Tabs ===== */}
          <ViewToggle view={view} onChange={setView} />

          {isMobile ? (
            // ===== Mobile Layout =====
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {view === 'business' ? 'Business Dashboard' : 'Dashboard'}
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() => setSidebarOpen(true)}
                  sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              {/* Businesses banner */}
              {view === 'business' && hasBusinesses && (
                <MyBusinessesBanner businesses={myBusinesses} navigate={navigate} />
              )}

              <MainActionsSection
                actions={view === 'business' ? BUSINESS_MAIN_ACTIONS : DISCOVERY_MAIN_ACTIONS}
              />

              <Fab
                color="primary"
                sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
                onClick={() => setSidebarOpen(true)}
              >
                <InfoIcon />
              </Fab>

              <MobileDashboardSidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                user={user}
                userData={userData}
                navigate={navigate}
                onAction={handleGetStartedAction}
                handleSuggestionAction={handleSuggestionAction}
                view={view}
              />
            </>
          ) : (
            // ===== Desktop Layout =====
            <Grid container spacing={3}>

              {/* Left Column */}
              <Grid item xs={12} md={3}>
                {view === 'business'
                  ? (
                    <BusinessProfilePanel
                      user={user}
                      navigate={navigate}
                      getInitials={getInitials}
                    />
                  ) : (
                    <>
                      <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
                          onClick={() => navigate(`/profile/${user._id}/active`)}
                        >
                          <Avatar sx={{ width: 80, height: 80, mr: 2 }} src={user.profileImageUrl || undefined} alt={user.displayName}>
                            {!user.profileImageUrl && getInitials(user.displayName)}
                          </Avatar>
                          <Box>
                            <Typography variant="h6">{user.displayName}</Typography>
                            <Typography variant="body2" color="text.secondary">New Member</Typography>
                          </Box>
                        </Box>
                        <LinearProgress variant="determinate" value={user.profileComplete || 0} sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {user.profileComplete || 0}% Complete
                        </Typography>
                      </Paper>
                      <GetStartedSection onAction={handleGetStartedAction} userData={userData} />
                    </>
                  )
                }
              </Grid>

              {/* Middle Column */}
              <Grid item xs={12} md={6}>
                {view === 'business' && hasBusinesses && (
                  <MyBusinessesBanner businesses={myBusinesses} navigate={navigate} />
                )}
                <MainActionsSection
                  actions={view === 'business' ? BUSINESS_MAIN_ACTIONS : DISCOVERY_MAIN_ACTIONS}
                />
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={3}>
                {view === 'business'
                  ? <BusinessSetupChecklist />
                  : (
                    <>
                      <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
                        <Typography variant="h6" gutterBottom>Suggested For You</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {SUGGESTED_ACTIONS.map((action, index) => {
                            const isUnlocked = action.isUnlocked(
                              user.connections || user.location || user.reviews || 0
                            );
                            return (
                              <Paper
                                key={index}
                                variant="outlined"
                                onClick={() => isUnlocked && handleSuggestionAction(action.id)}
                                sx={{
                                  p:        2,
                                  border:   'none',
                                  bgcolor:  isUnlocked ? 'grey.50' : 'grey.200',
                                  cursor:   isUnlocked ? 'pointer' : 'not-allowed',
                                  opacity:  isUnlocked ? 1 : 0.6,
                                  '&:hover': { bgcolor: isUnlocked ? 'grey.100' : 'grey.200' }
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: isUnlocked ? 600 : 400 }}>
                                  {action.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {action.description}
                                </Typography>
                                {!isUnlocked && (
                                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                    {action.goalMessage}
                                  </Typography>
                                )}
                              </Paper>
                            );
                          })}
                        </Box>
                      </Paper>
                      <NextGoalsSection userData={userData} />
                    </>
                  )
                }
              </Grid>

            </Grid>
          )}
        </Container>

        <LocationModal
          open={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          onLocationSet={handleLocationSet}
          userData={userData}
          setUserData={setUserData}
        />
      </Box>
    </>
  );
}